import React from 'react';

import {
  IAssigneeBasic,
  IBRNBasic,
  IDocumentChecklistItem,
  IRequestStatus,
  IRequestStatusMap,
  ListingOfferChannel,
} from '../../declarations';
import getCurrencyString from '../../helpers/getCurrencyString';
import Divider from '../common/Divider';
import SectionCard from '../common/SectionCard/SectionCard';
import SectionCardContent from '../common/SectionCard/SectionCardContent';
import SellerResponseAcceptanceFlowBox from './SellerResponseAcceptanceFlowBox';
import SellerResponseRepairItems from './SellerResponseRepairItems';

export interface ISellerResponseSectionProps {
  brn?: IBRNBasic;
  canGenerateSellerResponse: boolean;
  channel: ListingOfferChannel;
  creditAddendumItem?: IDocumentChecklistItem;
  generateSellerResponseRequestStatus?: IRequestStatus;
  onDocumentDownload: (id: number) => void;
  onDocumentRemove: (id: number) => void;
  onDocumentSave: (
    name: string,
    documentType: string,
    fileBase64DataUrl: string,
    requestId: number,
    checklistItemId?: number,
  ) => void;
  onRequestGenerateSellerResponse: () => void;
  removeChecklistDocumentRequests: IRequestStatusMap;
  resaleAgentLead: IAssigneeBasic;
  sellerResponseItem?: IDocumentChecklistItem;
  uploadChecklistDocumentRequests: IRequestStatusMap;
}

export default class SellerResponseSection extends React.PureComponent<
  ISellerResponseSectionProps
> {
  public renderNoRepairItems(): JSX.Element {
    const { sellerResponseItem, brn } = this.props;

    if (brn != null) {
      return (
        <SectionCardContent>
          We are reviewing your repair documents, and will respond here with updates.
        </SectionCardContent>
      );
    }

    if (sellerResponseItem && sellerResponseItem.document) {
      return null;
    }

    return (
      <SectionCardContent>
        Once we receive your repair documents, we'll review them and respond here with updates.
      </SectionCardContent>
    );
  }

  public renderRepairItems(): JSX.Element {
    const { brn, canGenerateSellerResponse, sellerResponseItem } = this.props;

    return (
      <div>
        {sellerResponseItem && sellerResponseItem.document ? (
          <SectionCardContent>
            We have responded to your requests below. If you're happy with the responses,
            {canGenerateSellerResponse
              ? ' press the button at the bottom of this section to proceed. '
              : ' scroll to the bottom of this section and follow the instructions. '}
            Otherwise, please contact your Experience Partner.
          </SectionCardContent>
        ) : (
          <SectionCardContent>We have responded to your requests below.</SectionCardContent>
        )}
        <SectionCardContent>
          {brn && (
            <div>
              <SellerResponseRepairItems brnRepairItems={brn.accepted_brn_repair_items} />
              {!!brn.credit_applied_as_concession_cents && (
                <div>
                  <Divider />
                  <div className="row between-xs large-margin-top large-margin-bottom">
                    <div className="col-xs start-xs">
                      <h5>Total Concession Credit:</h5>
                    </div>
                    <div className="col-xs end-xs">
                      {getCurrencyString(brn.credit_applied_as_concession_cents)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </SectionCardContent>
      </div>
    );
  }

  public repairItemsReadyToShow = () => {
    const { brn } = this.props;
    return brn && brn.post_review && brn.accepted_brn_repair_items.length;
  };

  public renderResponseFlowSection = () => {
    const {
      canGenerateSellerResponse,
      channel,
      creditAddendumItem,
      generateSellerResponseRequestStatus,
      onDocumentDownload,
      onDocumentRemove,
      onDocumentSave,
      onRequestGenerateSellerResponse,
      removeChecklistDocumentRequests,
      resaleAgentLead,
      sellerResponseItem,
      uploadChecklistDocumentRequests,
    } = this.props;

    if (!this.repairItemsReadyToShow() && !(sellerResponseItem && sellerResponseItem.document)) {
      return null;
    }

    return (
      <SectionCardContent>
        <SellerResponseAcceptanceFlowBox
          canGenerateSellerResponse={canGenerateSellerResponse}
          channel={channel}
          creditAddendumItem={creditAddendumItem}
          generateSellerResponseRequestStatus={generateSellerResponseRequestStatus}
          onDocumentDownload={onDocumentDownload}
          onDocumentRemove={onDocumentRemove}
          onDocumentSave={onDocumentSave}
          onRequestGenerateSellerResponse={onRequestGenerateSellerResponse}
          removeChecklistDocumentRequests={removeChecklistDocumentRequests}
          resaleAgentLead={resaleAgentLead}
          sellerResponseItem={sellerResponseItem}
          uploadChecklistDocumentRequests={uploadChecklistDocumentRequests}
        />
      </SectionCardContent>
    );
  };

  public render(): JSX.Element {
    const repairItemsSection = this.repairItemsReadyToShow()
      ? this.renderRepairItems()
      : this.renderNoRepairItems();

    const responseFlowSection = this.renderResponseFlowSection();

    return (
      <SectionCard
        id="seller-response-section"
        className="seller-response-brn-section"
        headerText="Seller Response"
      >
        {repairItemsSection}
        {repairItemsSection && responseFlowSection && <Divider />}
        {responseFlowSection}
      </SectionCard>
    );
  }
}
